import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";

interface BulkDeleteDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDealIds: string[];
  onDelete: () => Promise<void>;
  isLoading?: boolean;
}

const CONFIRM_PHRASE = "DELETE";

export const BulkDeleteDialog = ({
  isOpen,
  onClose,
  selectedDealIds,
  onDelete,
  isLoading = false,
}: BulkDeleteDialogProps) => {
  const [confirmText, setConfirmText] = useState("");
  const count = selectedDealIds.length;
  const isConfirmed = confirmText === CONFIRM_PHRASE;

  // Reset input each time dialog reopens.
  useEffect(() => {
    if (isOpen) setConfirmText("");
  }, [isOpen]);

  const handleDelete = async () => {
    if (!isConfirmed) return;
    await onDelete();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete {count} {count === 1 ? "deal" : "deals"}?
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. It will permanently remove deal
            information, notes, AI-generated reports, and all associated files.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <label
            htmlFor="bulk-delete-confirm-input"
            className="text-sm text-foreground"
          >
            Type <span className="font-mono font-semibold">DELETE</span> to
            confirm:
          </label>
          <Input
            id="bulk-delete-confirm-input"
            autoFocus
            autoComplete="off"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="DELETE"
            aria-describedby="bulk-delete-confirm-help"
            className="font-mono"
            disabled={isLoading}
          />
          <p
            id="bulk-delete-confirm-help"
            className="text-xs text-muted-foreground"
          >
            Case-sensitive. The button enables when the text matches exactly.
          </p>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={isLoading || !isConfirmed}
            variant="destructive"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete {count === 1 ? "deal" : "deals"}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
